import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import AppContext from '../AppContext/AppContext';
import { placeholderTexts } from '../Header/Header';

function Search() {
  const { state } = useContext(AppContext);
  const { user } = state;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [placeholder, setPlaceholder] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  // Only show on find-inspiration page
  const shouldShow = location.pathname === '/find-inspiration/';

  // Set search query from URL parameter
  useEffect(() => {
    const queryParam = searchParams.get('q');
    if (queryParam) {
      setSearchQuery(queryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user || user.status !== 'active' || !shouldShow) return;

    const currentText = placeholderTexts[placeholderIndex];
    
    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (placeholder.length < currentText.length) {
          setPlaceholder(currentText.substring(0, placeholder.length + 1));
          setTypingSpeed(100);
        } else {
          // Finished typing, wait then start deleting
          setTypingSpeed(2000);
          setIsDeleting(true);
        }
      } else {
        // Deleting
        if (placeholder.length > 0) {
          setPlaceholder(currentText.substring(0, placeholder.length - 1));
          setTypingSpeed(50);
        } else {
          // Finished deleting, move to next text
          setIsDeleting(false);
          setPlaceholderIndex((prevIndex) => (prevIndex + 1) % placeholderTexts.length);
          setTypingSpeed(500);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [placeholder, isDeleting, placeholderIndex, typingSpeed, user, shouldShow]);

  const isUrl = (text: string): boolean => {
    // Check if the text looks like a URL
    const urlPattern = /^(https?:\/\/)|(www\.)|([a-zA-Z0-9-]+\.[a-zA-Z]{2,})/;
    return urlPattern.test(text.trim());
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    
    if (!query) {
      return;
    }

    // Check if user entered a URL
    if (isUrl(query)) {
      alert('This feature is for finding website inspiration by description.\n\nPlease go to the projects page and Add new project.');
      return;
    }

    navigate(`/find-inspiration/?q=${encodeURIComponent(query)}`);
  };

  if (!user || user.status !== 'active' || !shouldShow) {
    return null;
  }

  return (
    <div className="dashboard__search">
      <form className="header__search" onSubmit={handleSearch}>
        <input
          type="text"
          className="header__search-input"
          placeholder={placeholder + '|'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Button 
          type="submit" 
          variant="contained"
          className="header__button"
          startIcon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L9.5 9.5L2 12L9.5 14.5L12 22L14.5 14.5L22 12L14.5 9.5L12 2Z" fill="currentColor"/>
              <path d="M5 3L4 6L1 7L4 8L5 11L6 8L9 7L6 6L5 3Z" fill="currentColor"/>
              <path d="M19 16L18.5 17.5L17 18L18.5 18.5L19 20L19.5 18.5L21 18L19.5 17.5L19 16Z" fill="currentColor"/>
            </svg>
          }
        >
          Find website inspiration
        </Button>
      </form>
    </div>
  );
}

export default Search;

